import { Image } from 'react-bootstrap';
import { IconWithTypography } from 'Components/molecules';
import { Typography, CustomTooltip } from 'Components/atoms';
import { autonomyScoreLabel } from 'Core-utils/staticData/staticAgents';
import archIcon from 'Assets/icons/arch.svg';
import clockIcon from 'Assets/icons/clock.svg';
import departmentIcon from 'Assets/icons/department.svg';
import personIcon from 'Assets/icons/person.svg';
import './styles.scss';

export interface AgentHeaderProps {
    agentId: string;
    ownerName: string;
    ownerEmail: string;
    ownerDepartment: string;
    vendor: string;
    harness: string;
    model: string;
    discoveredAt: string;
    lastRanAt: string;
    certifiedByOwner: boolean;
    autonomyScore: number;
}

const AVATAR_UNCERTIFIED =
    'https://images.ctfassets.net/a4bu6lgkooz7/2plOeC9henxNjEHOFgJoeN/a1fb8d3a6e8c6ea07667b04cb7044cf3/ai-avatar-1.png';
const AVATAR_CERTIFIED =
    'https://images.ctfassets.net/a4bu6lgkooz7/35FoX5hlLZydCgTV6H0CBJ/d10a02d682adde47c65449c2811da223/ai-avatar-2.png';

interface InfoRowProps {
    icon: React.ReactNode;
    label: string;
    value: string;
}

const InfoRow = ({ icon, label, value }: InfoRowProps) => (
    <div className="user-header__item d-flex flex-row align-items-center">
        <div className="user-header__styled-category d-flex justify-content-between">
            <IconWithTypography
                isRow={true}
                startIcon={icon}
                label={label}
                labelColor="text-medium-emphasis"
                labelVariant="body3"
            />
            <div className="colon">
                <Typography variant="body3" color="text-medium-emphasis">
                    :
                </Typography>
            </div>
        </div>
        <CustomTooltip label={value ?? ''}>
            <Typography
                variant="body2"
                style={{ width: '10.5rem' }}
                className="text-truncate"
                color="text-medium-emphasis"
            >
                {value || '-'}
            </Typography>
        </CustomTooltip>
    </div>
);

/** Col 1 — avatar + agent ID + autonomy score */
const AgentAvatarColumn = ({
    agentId,
    certifiedByOwner,
    autonomyScore,
}: {
    agentId: string;
    certifiedByOwner: boolean;
    autonomyScore: number;
}) => {
    const avatarSrc = certifiedByOwner ? AVATAR_CERTIFIED : AVATAR_UNCERTIFIED;
    const avatarAlt = certifiedByOwner ? 'Certified agent' : 'Uncertified agent';
    return (
        <div className="user-header__column d-flex">
            <div className="d-flex flex-row align-items-center justify-content-center">
                <div className="user-header__avatar-icon agent-header__avatar">
                    <img src={avatarSrc} alt={avatarAlt} className="agent-header__ampy-image" />
                </div>
                <div className="user-header__column d-flex flex-column justify-content-center">
                    <div className="user-header__header-container d-flex mb-1">
                        <CustomTooltip label={agentId ?? '-'}>
                            <Typography variant="h4" color="text-high-emphasis" className="user-header__typography">
                                {agentId || '-'}
                            </Typography>
                        </CustomTooltip>
                    </div>
                    <div className="agent-header__score d-flex align-items-center gap-1">
                        <Typography variant="body3" color="text-medium-emphasis">
                            Autonomy Score:
                        </Typography>
                        <Typography variant="body3" color="text-high-emphasis" className="agent-header__score-value">
                            {autonomyScore}
                        </Typography>
                        <Typography variant="body3" color="text-medium-emphasis">
                            &mdash; {autonomyScoreLabel(autonomyScore)}
                        </Typography>
                    </div>
                </div>
            </div>
        </div>
    );
};

/** Col 2 — owner name + department + vendor */
const OwnerColumn = ({ ownerName, ownerDepartment, vendor }: { ownerName: string; ownerDepartment: string; vendor: string }) => (
    <div className="user-header__column user-header__value-container d-flex flex-column">
        <InfoRow icon={<Image src={personIcon} className="user-header__icon" />} label="Owner" value={ownerName} />
        <InfoRow
            icon={<Image src={departmentIcon} className="user-header__icon" />}
            label="Department"
            value={ownerDepartment}
        />
        <InfoRow icon={<Image src={archIcon} className="user-header__icon" />} label="Vendor" value={vendor} />

    </div>
);

/** Col 3 — discovered date + model + harness */
const AgentMetaColumn = ({ discoveredAt, model, harness }: { discoveredAt: string; model: string; harness: string }) => (
    <div className="user-header__column user-header__value-container d-flex flex-column">
        <InfoRow
            icon={<Image src={clockIcon} className="user-header__icon" />}
            label="Discovered"
            value={discoveredAt}
        />
        <InfoRow icon={<Image src={archIcon} className="user-header__icon" />} label="Harness" value={harness} />
        <InfoRow icon={<Image src={archIcon} className="user-header__icon" />} label="Model" value={model} />
    </div>
);

/** Col 4 — last ran */
const LastRanColumn = ({ lastRanAt }: { lastRanAt: string }) => {
    const formatted = lastRanAt ? new Date(lastRanAt).toLocaleString() : '-';
    return (
        <div className="user-header__column user-header__value-container d-flex flex-column">
            <InfoRow
                icon={<Image src={clockIcon} className="user-header__icon" />}
                label="Last Ran"
                value={formatted}
            />
        </div>
    );
};

const AgentHeader = (props: AgentHeaderProps) => {
    const {
        agentId,
        ownerName,
        ownerEmail,
        ownerDepartment,
        vendor,
        harness,
        model,
        discoveredAt,
        lastRanAt,
        certifiedByOwner,
        autonomyScore,
    } = props;

    return (
        <div className="user-header__container d-flex bg-text-black" data-testid="agent_header">
            <AgentAvatarColumn agentId={agentId} certifiedByOwner={certifiedByOwner} autonomyScore={autonomyScore} />
            <div className="user-header__divider bg-structural-stroke-100" />
            <OwnerColumn ownerName={ownerName + ' ' + ownerEmail} ownerDepartment={ownerDepartment} vendor={vendor} />
            <div className="user-header__divider bg-structural-stroke-100" />
            <AgentMetaColumn discoveredAt={discoveredAt} model={model} harness={harness} />
            <div className="user-header__divider bg-structural-stroke-100" />
            <LastRanColumn lastRanAt={lastRanAt} />
        </div>
    );
};

export default AgentHeader;
